import React, { useEffect, useMemo, useState } from "react";
import { Modal, Table, Form, Button } from "react-bootstrap";
import { connect } from "react-redux";
import DatePicker from "react-datepicker";
import axios from "axios";
import { API_URL } from "../../../../config";
import { exportRowsToExcel } from "./exportToExcel";
import "./NotShippedInvoices.css";

// "Not Invoiced" widget — a big circular button showing what share of
// non-Commercial sales orders are fully shipped but still uninvoiced more than a
// few days past their install date.
//
// Data: GET /Dashboard/api/Dashboard/NotShippedInvoices -> result[0] = {
//   latePercent, lateCount, totalOrderCount, shippedCount, graceDays,
//   cutoffDate, orders[]
// }
//
// An order leaves BC's Sales Header once completely invoiced, so a completely
// shipped order that is still present is waiting to be billed. That makes this a
// snapshot of what is late right now, not a history of invoices that went out
// late — an invoice raised today drops its order from the figure entirely.
//
// Red once more than this many invoices are overdue. Applied to the count rather
// than the percentage, matching the other outstanding-work circles.
const ALERT_THRESHOLD = 10;

const COLUMNS = [
  { key: "jobNo", label: "Job No." },
  { key: "unitNumber", label: "Unit No." },
  { key: "installationDate", label: "Installation Date", type: "date" },
  // Business days, not calendar days — the API counts weekdays only, so this is
  // working opportunity elapsed rather than wall-clock age.
  { key: "daysLate", label: "Business Days Late", type: "number" },
  { key: "storeName", label: "Store Name", type: "select" },
  { key: "costCenter", label: "Cost Center", type: "select" },
  { key: "customerName", label: "Customer" },
  { key: "jobDescription", label: "Job Description" },
];

const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())}-${d.getFullYear()}`;
};

// Compare on the calendar date only — the API returns midnight timestamps, so
// including the time would make an end-of-range date exclude its own day.
const dayOf = (v) => {
  const d = new Date(v);
  return isNaN(d.getTime())
    ? null
    : new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// One filter row beneath the headers, matching the Existing Orders list: free
// text for most columns, a dropdown of the values present for low-cardinality
// ones, and a range picker for dates. Client-side because the list is small.
const FilterableTable = ({ columns, rows, emptyText, exportName }) => {
  const [text, setText] = useState({});
  const [range, setRange] = useState({});

  const options = useMemo(() => {
    const acc = {};
    columns
      .filter((c) => c.type === "select")
      .forEach((c) => {
        acc[c.key] = Array.from(
          new Set(rows.map((r) => r[c.key]).filter((v) => v))
        ).sort();
      });
    return acc;
  }, [columns, rows]);

  const filtered = useMemo(
    () =>
      rows.filter((row) =>
        columns.every((c) => {
          if (c.type === "date") {
            const [from, to] = range[c.key] || [null, null];
            if (!from && !to) return true;
            const v = dayOf(row[c.key]);
            if (v === null) return false;
            if (from && v < dayOf(from)) return false;
            if (to && v > dayOf(to)) return false;
            return true;
          }
          const q = (text[c.key] || "").trim().toLowerCase();
          if (!q) return true;
          return String(row[c.key] ?? "")
            .toLowerCase()
            .includes(q);
        })
      ),
    [rows, columns, text, range]
  );

  return (
    <>
      <div className="nsi-modal-toolbar">
        <div className="nsi-result-count">
          Showing {filtered.length} of {rows.length}
        </div>
        {/* Export exactly what the grid currently shows (filtered rows). */}
        <Button
          size="sm"
          variant="success"
          className="nsi-export-btn"
          disabled={filtered.length === 0}
          onClick={() =>
            exportRowsToExcel({
              columns,
              rows: filtered,
              fileName: exportName,
              sheetName: exportName,
            })
          }
        >
          Export to Excel
        </Button>
      </div>
      <div className="nsi-modal-scroll">
        <Table striped bordered hover size="sm" className="nsi-modal-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
            </tr>
            <tr className="nsi-filter-row">
              {columns.map((c) => (
                <th key={c.key}>
                  {c.type === "date" ? (
                    <DatePicker
                      selectsRange
                      startDate={(range[c.key] || [])[0]}
                      endDate={(range[c.key] || [])[1]}
                      onChange={(upd) =>
                        setRange((p) => ({ ...p, [c.key]: upd }))
                      }
                      dateFormat="MM-dd-yyyy"
                      placeholderText="MM-DD-YYYY - MM-DD-YYYY"
                      className="form-control form-control-sm"
                      isClearable
                    />
                  ) : c.type === "select" ? (
                    <Form.Select
                      size="sm"
                      value={text[c.key] || ""}
                      onChange={(e) =>
                        setText((p) => ({ ...p, [c.key]: e.target.value }))
                      }
                    >
                      <option value="">Select</option>
                      {(options[c.key] || []).map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Form.Select>
                  ) : (
                    <Form.Control
                      size="sm"
                      value={text[c.key] || ""}
                      onChange={(e) =>
                        setText((p) => ({ ...p, [c.key]: e.target.value }))
                      }
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="nsi-modal-empty">
                  {emptyText}
                </td>
              </tr>
            ) : (
              filtered.map((row, i) => (
                <tr key={row.orderNo || i}>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      className={c.type === "number" ? "nsi-num" : undefined}
                    >
                      {c.type === "date" ? fmtDate(row[c.key]) : row[c.key] || ""}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const NotShippedInvoices = ({ auth: { auth } }) => {
  // null = not loaded / failed. Distinguished from 0 via `status` so a failed
  // request never renders as a calm green zero.
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [showDetails, setShowDetails] = useState(false);

  const token = auth && auth[0] ? auth[0].token : null;

  useEffect(() => {
    if (!token) return;
    // Guard against setting state after the widget unmounts (the dashboard can
    // re-render/unmount widgets while this request is still in flight).
    let active = true;
    axios
      .get(`${API_URL}/Dashboard/api/Dashboard/NotShippedInvoices`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!active) return;
        const rows = Array.isArray(res.data)
          ? res.data
          : (res.data && res.data.result) || [];
        setData(rows[0] || null);
        setStatus(rows[0] ? "ok" : "error");
      })
      .catch((err) => {
        if (!active) return;
        console.warn("Could not load Not Invoiced:", err.message);
        setStatus("error");
      });
    return () => {
      active = false;
    };
  }, [token]);

  const loaded = status === "ok" && data;
  const orders = (loaded && data.orders) || [];
  const lateCount = loaded ? Number(data.lateCount) || 0 : null;
  const totalCount = loaded ? Number(data.totalOrderCount) || 0 : null;
  const graceDays = loaded ? Number(data.graceDays) || 0 : null;
  // Days this year that ended with at least one invoice overdue. Recorded forward
  // from the day the daily check first ran, so it reads 0 on a fresh install.
  const missedDays = loaded ? Number(data.missedDaysYtd) || 0 : null;
  const isAlert = loaded && lateCount > ALERT_THRESHOLD;

  const stateClass = !loaded ? "nsi-unknown" : isAlert ? "nsi-alert" : "nsi-calm";

  return (
    <div className="nsi-widget">
      <button
        type="button"
        className={`nsi-button ${stateClass}`}
        onClick={() => loaded && setShowDetails(true)}
        disabled={!loaded}
        title={
          loaded
            ? "View orders awaiting invoicing"
            : "Not Invoiced is unavailable"
        }
      >
        {isAlert && (
          <span className="nsi-icon" role="img" aria-label="alert">
            🧾
          </span>
        )}
        <span className="nsi-label">Not Invoiced</span>
        {/* Headline is days missed YTD; the current overdue count sits beneath it. */}
        <span className="nsi-percent">
          {status === "loading" ? "…" : loaded ? missedDays : "—"}
        </span>
        <span className="nsi-percent-caption">
          {missedDays === 1 ? "day missed YTD" : "days missed YTD"}
        </span>
        <span className="nsi-subcount">
          {status === "loading"
            ? ""
            : loaded
            ? `${lateCount} of ${totalCount} to invoice`
            : ""}
        </span>
      </button>

      {loaded && (
        <div className="nsi-subnote">
          Shipped, uninvoiced more than {graceDays} business days past install
        </div>
      )}

      <Modal
        show={showDetails}
        onHide={() => setShowDetails(false)}
        size="xl"
        centered
        scrollable
        dialogClassName="nsi-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Not Invoiced</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h5 className="nsi-section-title">
            Shipped Orders Awaiting Invoice ({orders.length})
          </h5>
          <FilterableTable
            columns={COLUMNS}
            rows={orders}
            emptyText="No orders are awaiting invoicing past the grace period."
            exportName="Not Invoiced"
          />
        </Modal.Body>
      </Modal>
    </div>
  );
};

const mapStateToProps = ({ auth }) => ({ auth });

export default connect(mapStateToProps)(NotShippedInvoices);
